import { select, call, takeLatest, put } from 'redux-saga/effects';

import { OrderRemainingHoursReportActions } from '../constants';
import { OrderRemainingHoursReportApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';
import {
    DeleteOrderRemainingHoursReportAction,
    GetOrderRemainingHoursReportAction,
    GetOrderRemainingHoursReportsAction,
} from '../actions/orderRemainingHoursReport-actions';
import sortingService from '../../services/sorting';
import { handleError, resolveApiCall, ResolverApiFailure, successType } from '../../services/api-handlers/api-resolver';
import { groupOrderRemainingToCsv } from '../../helpers/groupCsvOrderRemaining';
import { exportToCsv } from '../../services/exports/csv';
import { toast } from 'react-toastify';
import { groupOrderRemainingTotal } from '../../helpers/groupTotalOrderRemaining';

function* handleFetchOrderRemainingHoursReports(action: GetOrderRemainingHoursReportsAction) {
    const oldData = yield select((state: StoreState) => state.orderRemainingHoursReports.data);
    const { filters, sorting, page, itemsPerPage, notTotal } = action.payload;

    if (!notTotal) {
        const actionFiles: GetOrderRemainingHoursReportsAction = {
            type: OrderRemainingHoursReportActions.FETCH_ORDER_REMAINING_HOURS_REPORTS_TOTALS,
            payload: {
                filters,
                page: 1,
            },
        };
        yield call(handleFetchOrderRemainingHoursReportsTotal, actionFiles);
    }

    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        OrderRemainingHoursReportApi.getOrderRemainingHoursReports({
            ...filters,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchOrderRemainingHoursReportsTotal(action: GetOrderRemainingHoursReportsAction): any {
    const { filters, sorting, itemsPerPage } = action.payload;
    let page: number = action?.payload?.page ? +action.payload.page : 1;

    yield resolveApiCall(
        action,
        () =>
            OrderRemainingHoursReportApi.getOrderRemainingHoursReports({
                ...filters,
                order: sortingService.makeOrder(sorting),
                page,
                itemsPerPage,
                pagination: itemsPerPage === -1 ? 0 : 1,
            }),
        function* (data) {
            if (data.payload.length === 80) {
                const actionFiles: GetOrderRemainingHoursReportsAction = {
                    type: OrderRemainingHoursReportActions.FETCH_ORDER_REMAINING_HOURS_REPORTS_TOTALS,
                    payload: {
                        ...filters,
                        page: ++page,
                        sorting,
                        itemsPerPage: 80,
                    },
                    all: action.all ? [...action.all, ...data.payload] : [...data.payload],
                };

                yield call(handleFetchOrderRemainingHoursReportsTotal, actionFiles);
            } else {
                if (action.all) {
                    const groupData = groupOrderRemainingTotal(action.all);
                    yield put({
                        type: successType(action.type),
                        payload: groupData,
                        total: true,
                    });
                } else {
                    const groupData = groupOrderRemainingTotal(data.payload);
                    yield put({
                        type: successType(action.type),
                        payload: groupData,
                        total: true,
                    });
                }
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleFetchOrderRemainingHoursReportsCsv(action: GetOrderRemainingHoursReportsAction): any {
    const { filters, sorting, itemsPerPage } = action.payload;
    let page: number = action?.payload?.page ? +action.payload.page : 1;

    yield resolveApiCall(
        action,
        () =>
            OrderRemainingHoursReportApi.getOrderRemainingHoursReports({
                ...filters,
                order: sortingService.makeOrder(sorting),
                page,
                itemsPerPage,
                pagination: itemsPerPage === -1 ? 0 : 1,
            }),
        function* (data) {
            if (data.payload.length === 5000) {
                const actionFiles: GetOrderRemainingHoursReportsAction = {
                    type: OrderRemainingHoursReportActions.FETCH_ORDER_REMAINING_HOURS_REPORTS_CSV,
                    payload: {
                        ...filters,
                        page: ++page,
                        sorting,
                        itemsPerPage: 5000,
                    },
                    all: action.all ? [...action.all, ...data.payload] : [...data.payload],
                };

                yield call(handleFetchOrderRemainingHoursReportsCsv, actionFiles);
            } else {
                const dataAll = action.all ? [...action.all, ...data.payload] : [...data.payload];
                if (action.all) {
                    const groupCsv = groupOrderRemainingToCsv(dataAll);
                    exportToCsv('order-remaining-hours.csv', groupCsv);
                } else {
                    const groupCsvData = groupOrderRemainingToCsv(data.payload);
                    exportToCsv('order-remaining-hours.csv', groupCsvData);
                }
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

function* handleFetchOrderRemainingHoursReport(action: GetOrderRemainingHoursReportAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => OrderRemainingHoursReportApi.getOrderRemainingHoursReport(id));
}

function* handleDeleteOrderRemainingHoursReport(action: DeleteOrderRemainingHoursReportAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => OrderRemainingHoursReportApi.deleteOrderRemainingHoursReport(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(
        OrderRemainingHoursReportActions.FETCH_ORDER_REMAINING_HOURS_REPORTS,
        handleFetchOrderRemainingHoursReports,
    );
    yield takeLatest(
        OrderRemainingHoursReportActions.FETCH_ORDER_REMAINING_HOURS_REPORTS_CSV,
        handleFetchOrderRemainingHoursReportsCsv,
    );

    yield takeLatest(
        OrderRemainingHoursReportActions.FETCH_ORDER_REMAINING_HOURS_REPORT,
        handleFetchOrderRemainingHoursReport,
    );
    yield takeLatest(
        OrderRemainingHoursReportActions.DELETE_ORDER_REMAINING_HOURS_REPORT,
        handleDeleteOrderRemainingHoursReport,
    );
    yield takeLatest(
        OrderRemainingHoursReportActions.FETCH_ORDER_REMAINING_HOURS_REPORTS_TOTALS,
        handleFetchOrderRemainingHoursReportsTotal,
    );
}
